import { computed, inject, Injectable } from '@angular/core';

import { LoginResult } from '@models/login-info';
import { SYSTEM_ROLE, USER_ROLE, UserInfo } from '@models/user';
import { ignoreElements } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private _authService = inject(AuthService);

  readonly user = this._authService.user;
  readonly loggedIn = this._authService.loggedIn;

  // Derived signals for roles
  readonly isAdmin = computed(() => this.hasSystemRole(SYSTEM_ROLE.ADMIN));
  readonly isPartner = computed(() => this.hasSystemRole(SYSTEM_ROLE.PARTNER));
  readonly isModeller = computed(() => this.hasSystemRole(SYSTEM_ROLE.MODELLER));
  readonly isExpert = computed(
    () =>
      this.hasSystemRole(SYSTEM_ROLE.ADMIN) ||
      this.hasSystemRole(SYSTEM_ROLE.MODELLER) ||
      this.hasUserRole(USER_ROLE.EXPERT),
  );

  private hasSystemRole(role: SYSTEM_ROLE): boolean {
    const user = this.user();
    return user?.system_roles?.includes(role) ?? false;
  }

  private hasUserRole(role: USER_ROLE): boolean {
    const user = this.user();
    return user?.user_role?.includes(role) ?? false;
  }

  login(email: string, password: string) {
    return this._authService.login(email, password).pipe(ignoreElements());
  }

  register(email: string, password: string) {
    return this._authService.registerUser(email, password).pipe(ignoreElements());
  }

  updateProfile(updatedInfo: UserInfo): Observable<LoginResult> {
    return this._authService.updateUser(updatedInfo);
  }

  logout(): void {
    this._authService.logout();
  }
}
